import { roundToTwoDecimals } from 'common/helpers/';
import {
  calculateJitterScore,
  getGrade,
  SCORE_WEIGHTS,
  scorePacketLossPercent,
  toFiniteNumber,
  toPercent,
} from 'features/analytics/helpers/callScoreCurves';
import type { CallStats } from 'features/analytics/interfaces/';

export interface DirectionalPathQuality {
  score?: number;
  grade: string;
  packetLossPercent?: number;
  jitterMs?: number;
}

export interface CallDirectionScores {
  /** Endpoint → station media, measured locally (inbound-rtp). */
  received: DirectionalPathQuality;
  /** Station → endpoint media, reported by the far end over RTCP (remote-inbound-rtp). */
  sent: DirectionalPathQuality;
}

const UNMEASURED_PATH: DirectionalPathQuality = { grade: 'I' };

const buildPathQuality = (
  packetLossPercent: number | undefined,
  jitterSeconds: number | undefined
): DirectionalPathQuality => {
  let weightedScore = 0;
  let totalWeight = 0;

  if (packetLossPercent !== undefined) {
    weightedScore +=
      scorePacketLossPercent(packetLossPercent) * SCORE_WEIGHTS.packetLoss;
    totalWeight += SCORE_WEIGHTS.packetLoss;
  }
  if (jitterSeconds !== undefined) {
    weightedScore += calculateJitterScore(jitterSeconds) * SCORE_WEIGHTS.jitter;
    totalWeight += SCORE_WEIGHTS.jitter;
  }

  if (totalWeight === 0) {
    return UNMEASURED_PATH;
  }

  const score = Math.round(weightedScore / totalWeight);

  return {
    score,
    grade: getGrade(score),
    packetLossPercent,
    jitterMs:
      jitterSeconds === undefined
        ? undefined
        : roundToTwoDecimals(Math.max(0, jitterSeconds) * 1000),
  };
};

const scoreReceivedPath = (summary: CallStats): DirectionalPathQuality => {
  const inbound = summary.inboundRtpVideo;
  const lost = toFiniteNumber(inbound?.packetsLost);
  const received = toFiniteNumber(inbound?.packetsReceived);
  const lossPercent =
    lost !== undefined && received !== undefined
      ? toPercent(lost, Math.max(0, lost) + received)
      : undefined;

  return buildPathQuality(lossPercent, toFiniteNumber(inbound?.jitter));
};

const scoreSentPath = (summary: CallStats): DirectionalPathQuality => {
  const remoteInbound = summary.remoteInboundRtpVideo;
  const remoteLost = toFiniteNumber(remoteInbound?.packetsLost);

  // packetsLost is always present on a real receiver report; without it the far end never reported back.
  if (remoteLost === undefined) {
    return UNMEASURED_PATH;
  }

  const packetsSent = toFiniteNumber(summary.outboundRtpVideo?.packetsSent);
  const fractionLost = toFiniteNumber(remoteInbound?.fractionLost);
  const cumulativeLossPercent =
    packetsSent !== undefined ? toPercent(remoteLost, packetsSent) : undefined;
  const lossPercent =
    cumulativeLossPercent ??
    (fractionLost === undefined
      ? undefined
      : roundToTwoDecimals(fractionLost * 100));

  return buildPathQuality(lossPercent, toFiniteNumber(remoteInbound?.jitter));
};

/**
 * Scores each media direction on its network components (packet loss, jitter) using the
 * CallScore curves and weights. RTT is round-trip so it is not directional.
 */
export const scoreCallDirections = (
  summary: CallStats
): CallDirectionScores => ({
  received: scoreReceivedPath(summary),
  sent: scoreSentPath(summary),
});
